// Per-profile persistence of the patient-portal Today page card order.
//
// State model: an ordered array of stable card keys (e.g. 'liveNow',
// 'medicines', …). The TodayPage renders cards in this order; cards whose
// data slice is null still take a slot in the array but render nothing.
//
// Persistence: localStorage keyed by profile id, so each PatientProfile gets
// its own preferred ordering. Reading is reconciled against the canonical
// list shipped with the build:
//   - keys removed from the canonical list are dropped from the resolved order
//     (a card that was once present but no longer ships shouldn't reserve a slot)
//   - keys added to the canonical list are appended at the end
//     (newly-shipped cards appear by default — additive forward compat)
//
// `isCustomizing` toggles a UI mode where each rendered card grows up/down
// arrow controls. We keep the mode in component state (not localStorage) —
// it's a transient "I want to rearrange right now" intent, not a setting.

import { useCallback, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY_PREFIX = 'patientPortal.todayCardOrder.'

// Canonical card order — matches docs/PATIENT_PORTAL_PLAN.md §7.1 with two
// additions: 'clinicQueue' at the very top (when an in-clinic appointment
// exists today, the queue position is the most urgent thing on the page) and
// 'sessionsTimeline' towards the bottom (combined "Sessions" card that
// shows Earlier today + Coming up — formerly two separate cards
// 'upcomingSessions' + 'pastSessionsToday' which are migrated by
// {@link migrateLegacyKeys} below).
//
// Stable keys — once shipped, these strings live forever in users' localStorage
// values. Renaming requires a migration entry, not a one-line change.
export const DEFAULT_TODAY_CARD_ORDER = Object.freeze([
  'clinicQueue',
  'crisisBanner',
  'dues',
  'blockedSessions',
  'liveNow',
  'nextSession',
  'medicines',
  'meals',
  'activities',
  'vitals',
  'mood',
  'unread',
  'sessionsTimeline',
  'packProgress',
  'goalsNudge',
])

/**
 * Translate legacy stored card keys into their current equivalents — for
 * users who customised order before a card was renamed / merged. Runs
 * before the canonical reconcile step so the merged card lands at the
 * position of whichever legacy key came first, not at the bottom.
 *
 * Current entries:
 *   - `upcomingSessions` + `pastSessionsToday` → `sessionsTimeline`
 *     (separate cards collapsed into one).
 */
function migrateLegacyKeys(stored) {
  if (!Array.isArray(stored) || stored.length === 0) return []
  const replacements = {
    upcomingSessions: 'sessionsTimeline',
    pastSessionsToday: 'sessionsTimeline',
  }
  const out = []
  const seen = new Set()
  for (const k of stored) {
    const target = replacements[k] || k
    if (seen.has(target)) continue
    seen.add(target)
    out.push(target)
  }
  return out
}

function readStoredOrder(profileId) {
  if (!profileId || typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_PREFIX + profileId)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : null
  } catch (e) {
    return null
  }
}

function writeStoredOrder(profileId, order) {
  if (!profileId || typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(STORAGE_KEY_PREFIX + profileId, JSON.stringify(order))
  } catch (e) {
    // strict-incognito etc — silent no-op; user just won't see the order persist
  }
}

function clearStoredOrder(profileId) {
  if (!profileId || typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.removeItem(STORAGE_KEY_PREFIX + profileId)
  } catch (e) {
    // ignore
  }
}

/**
 * Reconcile a stored order with the canonical list.
 * - Migrate legacy keys to their current equivalents (renames / merges)
 * - Drop stored keys that no longer appear in canonical (removed cards)
 * - Append canonical keys missing from stored (newly-shipped cards)
 * Returns a fresh array; never mutates inputs.
 */
function reconcile(stored, canonical) {
  const migrated = migrateLegacyKeys(stored)
  const canonSet = new Set(canonical)
  const valid = migrated.filter((k) => canonSet.has(k))
  const seen = new Set(valid)
  const missing = canonical.filter((k) => !seen.has(k))
  return [...valid, ...missing]
}

export function useTodayCardOrder(profileId, canonical = DEFAULT_TODAY_CARD_ORDER) {
  const [order, setOrderState] = useState(() =>
    reconcile(readStoredOrder(profileId), canonical),
  )
  const [isCustomizing, setIsCustomizing] = useState(false)

  // Re-read on profile switch — each profile has its own stored ordering.
  useEffect(() => {
    setOrderState(reconcile(readStoredOrder(profileId), canonical))
  }, [profileId, canonical])

  const persist = useCallback(
    (next) => {
      writeStoredOrder(profileId, next)
    },
    [profileId],
  )

  const setOrder = useCallback(
    (next) => {
      setOrderState((prev) => {
        const arr = typeof next === 'function' ? next(prev) : next
        persist(arr)
        return arr
      })
    },
    [persist],
  )

  const moveUp = useCallback(
    (key) => {
      setOrderState((prev) => {
        const idx = prev.indexOf(key)
        if (idx <= 0) return prev
        const nextArr = [...prev]
        const [item] = nextArr.splice(idx, 1)
        nextArr.splice(idx - 1, 0, item)
        persist(nextArr)
        return nextArr
      })
    },
    [persist],
  )

  const moveDown = useCallback(
    (key) => {
      setOrderState((prev) => {
        const idx = prev.indexOf(key)
        if (idx < 0 || idx >= prev.length - 1) return prev
        const nextArr = [...prev]
        const [item] = nextArr.splice(idx, 1)
        nextArr.splice(idx + 1, 0, item)
        persist(nextArr)
        return nextArr
      })
    },
    [persist],
  )

  const resetOrder = useCallback(() => {
    setOrderState(canonical.slice())
    clearStoredOrder(profileId)
  }, [canonical, profileId])

  const toggleCustomize = useCallback(() => setIsCustomizing((v) => !v), [])

  return useMemo(
    () => ({
      order,
      setOrder,
      moveUp,
      moveDown,
      resetOrder,
      isCustomizing,
      toggleCustomize,
      stopCustomize: () => setIsCustomizing(false),
    }),
    [order, setOrder, moveUp, moveDown, resetOrder, isCustomizing, toggleCustomize],
  )
}
